import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { api, formatDateTime, formatEUR, formatApiError } from "../lib/api";
import { Search, Download, FileText, Filter, Send, Settings, Clock, CheckSquare, CalendarDays, BellRing } from "lucide-react";
import { SearchableSelect } from "../components/SearchableSelect";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "pending", label: "Em análise", cls: "chip-pending" },
  { value: "processed", label: "Processadas", cls: "chip-processed" },
  { value: "error", label: "Com erro", cls: "chip-error" },
];

const MONTHS_PT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const monthLabel = (d) => {
  const m = /^(\d{4})-(\d{2})/.exec(d || "");
  return m && +m[2] >= 1 && +m[2] <= 12 ? `${MONTHS_PT[+m[2] - 1]} ${m[1]}` : null;
};

export default function AccountantDashboard() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [batches, setBatches] = useState([]);
  const [accountants, setAccountants] = useState([]);
  const [filters, setFilters] = useState({ status: "", company_id: "", accountant_id: "", month: "", q: "" });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [pendingCounts, setPendingCounts] = useState({ pending: 0, error: 0 });

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const companiesLoadedRef = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const f = filtersRef.current;
    const params = {};
    if (f.status) params.status = f.status;
    if (f.company_id) params.company_id = f.company_id;
    if (f.accountant_id) params.accountant_id = f.accountant_id;
    if (f.month) params.month = f.month;
    if (f.q) params.q = f.q;
    try {
      const [inv, comp] = await Promise.all([
        api.get("/invoices", { params }),
        companiesLoadedRef.current ? Promise.resolve(null) : api.get("/companies"),
      ]);
      setInvoices(inv.data);
      if (comp) {
        setCompanies(comp.data);
        companiesLoadedRef.current = true;
      }
      api.get("/toconline/batches").then(({ data }) => setBatches(data)).catch(() => {});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [filters.status, filters.company_id, filters.accountant_id, filters.month, fetchData]);

  useEffect(() => {
    if (user?.is_admin) api.get("/accountants").then(({ data }) => setAccountants(data)).catch(() => {});
  }, [user?.is_admin]);

  const loadPendingCounts = useCallback(() => {
    api.get("/invoices/pending-count").then(({ data }) => setPendingCounts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadPendingCounts();
    const id = setInterval(loadPendingCounts, 30000);
    return () => clearInterval(id);
  }, [loadPendingCounts]);

  const showPending = () => setFilters({ status: "pending", company_id: "", accountant_id: "", month: "", q: "" });

  const onSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const stats = useMemo(() => ({
    total: invoices.length,
    pending: invoices.filter((i) => i.status === "pending").length,
    processed: invoices.filter((i) => i.status === "processed").length,
    error: invoices.filter((i) => i.status === "error").length,
    sum: invoices.reduce((acc, i) => acc + (Number(i.extracted_fields?.total) || 0), 0),
  }), [invoices]);

  const downloadCsv = async () => {
    const f = filtersRef.current;
    const params = {};
    if (f.status) params.status = f.status;
    if (f.company_id) params.company_id = f.company_id;
    if (f.accountant_id) params.accountant_id = f.accountant_id;
    if (f.month) params.month = f.month;
    if (f.q) params.q = f.q;
    const resp = await api.get("/invoices/export/csv", { params, responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url; a.download = "faturas.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const toggleSel = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const pendingVisible = invoices.filter((i) => i.status === "pending");
  const allPendingSelected = pendingVisible.length > 0 && pendingVisible.every((i) => selected.has(i.id));
  const toggleSelectAll = () => {
    if (pendingVisible.length === 0) return;
    setSelected(allPendingSelected ? new Set() : new Set(pendingVisible.map((i) => i.id)));
  };

  const selectedInvoices = invoices.filter((i) => selected.has(i.id));
  const uniqueCompanies = new Set(selectedInvoices.map((i) => i.company_id).filter(Boolean));
  const canSend = selectedInvoices.length > 0 && uniqueCompanies.size === 1 && [...uniqueCompanies][0];

  const sendToTocOnline = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const { data } = await api.post("/toconline/send", { invoice_ids: [...selected] });
      toast.success(`Enviado! ${data.emails_sent} email(s) para ${data.to_email}. ${data.invoices_updated} fatura(s) marcada(s) como processada.`);
      setSendOpen(false);
      setSelected(new Set());
      fetchData();
      loadPendingCounts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="paper-grain min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="accountant-dashboard">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="label-eyebrow">portal contabilista</span>
            <h1 className="font-display text-3xl sm:text-4xl mt-2">Faturas recebidas</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={downloadCsv} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="export-csv-btn">
              <Download size={16} /> Exportar CSV
            </button>
            <Link to="/contabilista/calendario" className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="nav-calendar-btn">
              <CalendarDays size={16} /> Calendário fiscal
            </Link>
            <Link to="/contabilista/definicoes" className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="nav-settings-btn">
              <Settings size={16} /> Definições
            </Link>
          </div>
        </div>

        {(pendingCounts.pending > 0 || pendingCounts.error > 0) && (
          <button
            type="button"
            onClick={showPending}
            className="w-full mt-6 flex items-center gap-3 text-left rounded-xl border-2 border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 hover:bg-[#FEF3C7] transition-colors"
            data-testid="pending-banner"
          >
            <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F59E0B] text-white">
              <BellRing size={18} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-medium text-[#92400E]">
                {pendingCounts.pending > 0
                  ? `${pendingCounts.pending} fatura(s) à espera de processamento`
                  : `${pendingCounts.error} fatura(s) com erro a precisar de atenção`}
              </span>
              <span className="block text-sm text-[#B45309]">
                {pendingCounts.pending > 0 && pendingCounts.error > 0
                  ? `Inclui ${pendingCounts.error} com erro. `
                  : ""}
                Toque para ver as faturas pendentes.
              </span>
            </span>
            <span className="chip bg-[#F59E0B] text-white shrink-0">Ver pendentes</span>
          </button>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Stat label="Total" value={stats.total} testid="stat-total" />
          <Stat label="Em análise" value={stats.pending} testid="stat-pending" badge={pendingCounts.pending} onClick={showPending} />
          <Stat label="Processadas" value={stats.processed} testid="stat-processed" />
          <Stat label="Com erro" value={stats.error} testid="stat-error" />
        </div>

        {/* Filters */}
        <form onSubmit={onSearch} className="card-soft mt-6" data-testid="filters-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">Pesquisa</label>
              <div className="relative">
                <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-muted)] pointer-events-none" />
                <input
                  className="input-soft pr-9"
                  placeholder="Fornecedor, NIF, utilizador…"
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  data-testid="search-input"
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Empresa</label>
              <SearchableSelect
                value={filters.company_id}
                onChange={(v) => setFilters({ ...filters, company_id: v })}
                options={companies.map((c) => ({ value: c.id, label: `${c.name}${c.nif ? ` (${c.nif})` : ""}`, search: c.nif || "" }))}
                allLabel="Todas as empresas"
                placeholder="Todas as empresas"
                testid="company-filter"
              />
            </div>
            {user?.is_admin && (
              <div>
                <label className="label-eyebrow block mb-2">Colaborador</label>
                <SearchableSelect
                  value={filters.accountant_id}
                  onChange={(v) => setFilters({ ...filters, accountant_id: v })}
                  options={accountants.map((a) => ({ value: a.id, label: a.name, search: a.email || "" }))}
                  allLabel="Todos os colaboradores"
                  placeholder="Todos os colaboradores"
                  testid="accountant-filter"
                />
              </div>
            )}
            <div>
              <label className="label-eyebrow block mb-2">Mês da fatura</label>
              <input
                type="month"
                className="input-soft"
                value={filters.month}
                onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                data-testid="month-filter"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Estado</label>
              <select
                className="input-soft"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                data-testid="status-filter"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value || "all"} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button type="submit" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="apply-filters-btn">
                <Filter size={14} /> Aplicar
              </button>
            </div>
          </div>
        </form>

        {/* Table */}
        <div className="card-soft mt-6 p-0 overflow-hidden" data-testid="invoices-table-card">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-[color:var(--border)]">
            <button
              type="button"
              onClick={toggleSelectAll}
              disabled={pendingVisible.length === 0}
              className="btn-secondary text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="select-all-btn"
              title={pendingVisible.length === 0 ? "Sem faturas em análise nesta vista" : "Seleciona todas as faturas em análise para enviar para o TocOnline"}
            >
              <CheckSquare size={14} /> {allPendingSelected ? "Limpar seleção" : `Selecionar em análise (${pendingVisible.length})`}
            </button>
            <span className="text-xs text-[color:var(--ink-muted)]">
              {invoices.length === 0
                ? "Sem faturas nesta vista."
                : `${invoices.length} fatura(s) nesta vista · ${pendingVisible.length} em análise${filters.status ? ` · estado: ${STATUS_OPTIONS.find((o) => o.value === filters.status)?.label}` : ""}`}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[color:var(--paper)] text-[color:var(--ink-muted)] uppercase text-xs tracking-wider">
                  <th className="p-4 w-10"></th>
                  <th className="text-left p-4">Recebida</th>
                  <th className="text-left p-4">Utilizador</th>
                  <th className="text-left p-4">Fornecedor</th>
                  <th className="text-left p-4">NIF</th>
                  <th className="text-left p-4">Mês</th>
                  <th className="text-right p-4">Total</th>
                  <th className="text-left p-4">Estado</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="p-8 text-center text-[color:var(--ink-muted)]">A carregar…</td></tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-12 text-center" data-testid="empty-state">
                      <FileText className="mx-auto text-[color:var(--ink-muted)]" size={24} />
                      <div className="mt-3 text-[color:var(--ink-muted)]">Sem faturas para mostrar.</div>
                    </td>
                  </tr>
                ) : invoices.map((inv) => {
                  const ef = inv.extracted_fields || {};
                  const cls = inv.status === "processed" ? "chip-processed" : inv.status === "error" ? "chip-error" : "chip-pending";
                  const label = inv.status === "processed" ? "Processada" : inv.status === "error" ? "Erro" : "Em análise";
                  const isSel = selected.has(inv.id);
                  return (
                    <tr key={inv.id} className={`border-t border-[color:var(--border)] row-hover ${isSel ? "bg-[color:var(--paper)]" : ""}`} data-testid={`accountant-row-${inv.id}`}>
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleSel(inv.id)}
                          className="w-4 h-4 accent-[color:var(--primary)] cursor-pointer"
                          data-testid={`select-invoice-${inv.id}`}
                        />
                      </td>
                      <td className="p-4 font-mono-data text-xs">
                        {formatDateTime(inv.created_at)}
                        {inv.source === "email" && <span className="ml-2 chip chip-processed text-[10px]" data-testid={`source-email-${inv.id}`}>Email</span>}
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{inv.client_name}</div>
                        <div className="text-xs text-[color:var(--ink-muted)]">{inv.company || inv.client_email}</div>
                      </td>
                      <td className="p-4">{ef.vendor || <span className="text-[color:var(--ink-muted)]">—</span>}</td>
                      <td className="p-4 font-mono-data">{ef.nif || "—"}</td>
                      <td className="p-4" data-testid={`invoice-month-${inv.id}`}>
                        {monthLabel(ef.date) ? <span className="chip bg-[color:var(--paper)] border border-[color:var(--border)] text-[color:var(--ink)]">{monthLabel(ef.date)}</span> : <span className="text-[color:var(--ink-muted)]">—</span>}
                      </td>
                      <td className="p-4 text-right font-mono-data">{formatEUR(ef.total)}</td>
                      <td className="p-4"><span className={`chip ${cls}`}>{label}</span></td>
                      <td className="p-4 text-right">
                        <Link to={`/contabilista/fatura/${inv.id}`} className="text-[color:var(--primary)] font-medium underline-offset-4 hover:underline" data-testid={`open-invoice-${inv.id}`}>Abrir</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {batches.length > 0 && (
          <div className="card-soft mt-6 p-0 overflow-hidden" data-testid="toconline-history-card">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-[color:var(--border)]">
              <Clock size={16} className="text-[color:var(--primary)]" />
              <span className="label-eyebrow">Últimos envios para TocOnline</span>
            </div>
            <div className="divide-y divide-[color:var(--border)]">
              {batches.slice(0, 5).map((b) => (
                <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3" data-testid={`batch-row-${b.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{b.company_name}</div>
                    <div className="text-xs text-[color:var(--ink-muted)] font-mono-data truncate">{b.to_email}</div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-right">
                      <div className="label-eyebrow">Faturas</div>
                      <div className="font-mono-data">{(b.invoice_ids || []).length}</div>
                    </div>
                    <div className="text-right text-[color:var(--ink-muted)] font-mono-data text-xs whitespace-nowrap">
                      {formatDateTime(b.sent_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-[color:var(--border)] bg-[color:var(--surface)]/95 backdrop-blur px-4 sm:px-8 py-4 flex flex-wrap items-center justify-between gap-3 shadow-[0_-10px_30px_-15px_rgba(40,20,80,0.25)]" data-testid="selection-bar">
          <div className="text-sm">
            <span className="font-medium">{selected.size} fatura(s) selecionada(s)</span>
            <span className="text-[color:var(--ink-muted)] ml-3">
              {uniqueCompanies.size === 0 ? "" :
               uniqueCompanies.size === 1 ? `· empresa única: NIF ${selectedInvoices[0]?.company_nif || "—"}` :
               <span className="text-[#991B1B]">· selecione faturas de uma única empresa para enviar</span>}
            </span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setSelected(new Set())} className="btn-secondary text-sm" data-testid="clear-selection-btn">Limpar</button>
            <button
              onClick={() => canSend && setSendOpen(true)}
              disabled={!canSend || sending}
              className="btn-primary text-sm inline-flex items-center gap-2"
              data-testid="send-toconline-btn"
            >
              <Send size={14} /> {sending ? "A enviar…" : "Enviar para TocOnline"}
            </button>
          </div>
        </div>
      )}

      {/* TocOnline send confirm modal */}
      {sendOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 fade-in" onClick={() => !sending && setSendOpen(false)}>
          <div className="card-soft w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="send-toconline-modal">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">enviar para tocOnline</span>
              <button onClick={() => setSendOpen(false)} className="p-1 rounded hover:bg-[color:var(--border)]"><Send size={16} /></button>
            </div>
            <h2 className="font-display text-xl mt-2">Confirmar envio</h2>
            <p className="text-sm text-[color:var(--ink-muted)] mt-2">
              Enviar <span className="font-medium text-[color:var(--ink)]">{selected.size}</span> fatura(s) para{" "}
              <span className="font-mono-data">{selectedInvoices[0]?.company_nif}@my.toconline.pt</span>?
              As faturas enviadas serão marcadas como processadas.
            </p>
            <div className="mt-5 flex gap-2">
              <button onClick={sendToTocOnline} disabled={sending} className="btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="confirm-send-toconline-btn">
                <Send size={14} /> {sending ? "A enviar…" : "Enviar"}
              </button>
              <button onClick={() => setSendOpen(false)} disabled={sending} className="btn-secondary" data-testid="cancel-send-toconline-btn">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, badge, onClick }) {
  const content = (
    <>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        {badge > 0 && (
          <span className="min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center bg-[#F59E0B] text-white" data-testid={`${testid}-badge`}>
            {badge}
          </span>
        )}
      </div>
      <div className="font-display text-2xl sm:text-3xl mt-1">{value}</div>
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="card-soft p-4 text-left hover:border-[color:var(--primary)] transition-colors" data-testid={testid}>
        {content}
      </button>
    );
  }
  return (
    <div className="card-soft p-4" data-testid={testid}>
      {content}
    </div>
  );
}
